import React from 'react';
import { StyleSheet, View } from 'react-native';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyXSBoldText from 'shared/components/vxl/typography/primary/body/BodyXSBoldText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import theme from 'shared/styles/theme';

export interface MarkProps {
  id: string;
  status: 'Active' | 'Locked' | 'Completed' | 'Failed';
  borderColor: string;
  content?: string;
  caption: {
    label?: string;
    style: {
      color: string;
    };
  };
  iconKey: IconKeyType;

  tintColor: string;
}

interface MarkerProps {
  trackerState: MarkProps;
  trackerSize: number;
  position: number;
}

const Marker = ({ trackerState, trackerSize, position }: MarkerProps) => {
  const outerCircleStyle =
    trackerState?.status === 'Active'
      ? { borderColor: trackerState?.borderColor }
      : { borderColor: trackerState?.borderColor };

  const markerStyle =
    trackerState?.status === 'Active'
      ? { backgroundColor: trackerState?.tintColor }
      : { backgroundColor: trackerState?.tintColor };

  const contentStyle =
    trackerState?.status === 'Active'
      ? { color: trackerState?.caption?.style.color }
      : { color: trackerState?.caption?.style.color };

  const labelStyle =
    trackerState?.status === 'Active'
      ? { color: trackerState?.caption?.style.color }
      : { color: trackerState?.caption?.style.color };
  return (
    <View
      style={[
        styles.container,
        { width: handleOuterCircleSize(trackerSize).width },
        position < 1 && styles.position,
        position === trackerSize - 1 && styles.positionEnd,
      ]}
      key={trackerState?.id}
    >
      <View
        testID="circle-outer"
        style={[
          styles.circleOuter,
          outerCircleStyle,
          handleOuterCircleSize(trackerSize),
        ]}
      >
        {trackerState?.status === 'Active' ||
        trackerState?.status === 'Locked' ? (
          <View
            testID="circle-inner"
            style={[
              styles.circleInner,
              markerStyle,
              handleInnerCircleSize(trackerSize),
            ]}
          >
            <BodyBaseBoldText style={contentStyle} numberOfLines={0}>
              {trackerState?.content}
            </BodyBaseBoldText>
          </View>
        ) : null}
        {trackerState?.status === 'Completed' ||
        trackerState?.status === 'Failed' ? (
          <Icon
            iconKey={
              trackerState?.status === 'Failed'
                ? 'LIST_X_MARK'
                : 'LIST_CHECK_MARK'
            }
            iconColor={
              trackerState?.status === 'Failed'
                ? theme.color.text.tertiary
                : theme.color.interactive.primary
            }
            iconSize={trackerSize < 5 ? 44 : 33}
            iconAccessibilityLabel={
              trackerState?.status === 'Failed'
                ? 'LIST_X_MARK'
                : 'LIST_CHECK_MARK'
            }
          />
        ) : null}
      </View>
      {trackerState?.status === 'Active' ? (
        <BodyXSBoldText style={[styles.label, labelStyle]} numberOfLines={1}>
          {trackerState?.caption?.label}
        </BodyXSBoldText>
      ) : (
        <BodyXSText style={[styles.label, labelStyle]} numberOfLines={1}>
          {trackerState?.caption?.label}
        </BodyXSText>
      )}
    </View>
  );
};

const handleOuterCircleSize = (size: number) => {
  if (size < 5) {
    return styles.outerLarge;
  }
  return styles.outerSmall;
};

const handleInnerCircleSize = (size: number) => {
  if (size < 5) {
    return styles.innerLarge;
  }
  return styles.innerSmall;
};

const styles = StyleSheet.create({
  circleInner: {
    alignItems: 'center',
    borderRadius: 28,
    justifyContent: 'center',
  },

  circleOuter: {
    alignItems: 'center',
    borderColor: theme.color.border.focused,
    borderRadius: 28,
    borderWidth: 1,
    justifyContent: 'center',
  },
  container: {
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  innerLarge: {
    height: 44,
    width: 44,
  },
  innerSmall: {
    height: 33,
    width: 33,
  },
  label: {
    paddingHorizontal: theme.spacing.s,
  },
  outerLarge: {
    height: 56,
    width: 56,
  },
  outerSmall: {
    height: 45.17,
    width: 45.17,
  },
  position: {
    marginLeft: theme.spacing.m,
  },
  positionEnd: {
    marginRight: theme.spacing.m,
  },
});

export default Marker;
